var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createRemoteVideoMenuButtonEvent } from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import { configureConference } from '../base/conference/conferenceInfoWatch';
import { getCurrentConference } from '../base/conference/functions';
import { disconnectAny, setPreferVisitor } from '../base/connection/actions.any';
import { connect } from '../base/connection/actions.web';
import { getLocalParticipant } from '../base/participants/functions';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { CLEAR_VISITOR_PROMOTION_REQUEST, I_AM_VISITOR_MODE, SET_AUTO_PROMOTE_TOGGLE_ERROR, SET_AUTO_PROMOTE_TOGGLE_PENDING, SET_AUTO_PROMOTE_VISITORS, SET_I_AM_CHAT_SUSPENDED, SET_I_AM_HAND_BLOCKED, SET_PREJOIN_VISITORS_MODE, SET_RAISE_HAND_DISABLED_TOGGLE_ERROR, SET_RAISE_HAND_DISABLED_TOGGLE_PENDING, SET_ROLE_TRANSITION, SET_VISITORS_ENABLED, SET_VISITORS_RAISE_HAND_DISABLED, SET_VISITORS_ROSTER, SET_VISITORS_SUPPORTED, SET_VISITOR_CHAT_SUSPENDED, SET_VISITOR_DEMOTE_ACTOR, SET_VISITOR_HAND_BLOCKED, SET_VISITOR_MODE_TOGGLE_ERROR, SET_VISITOR_MODE_TOGGLE_PENDING, UPDATE_VISITORS_COUNT, VISITOR_JOINED, VISITOR_LEFT, VISITOR_PROMOTION_REQUEST } from './actionTypes';
import { VISITORS_METADATA_KEY } from './constants';
import { verr, vlog } from './debug';
/**
 * Action used to admit multiple participants in the conference.
 *
 * @param {Array<Object>} requests - A list of visitors requests.
 * @param {PromotionTargetRole} role - The main-room role to grant.
 * @returns {Function}
 */
export function admitMultiple(requests, role = 'participant') {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'promotion-response',
            approved: true,
            role,
            ids: requests.map(r => r.from)
        });
        requests.forEach(request => dispatch(clearPromotionRequest(request)));
    };
}
/**
 * Approves the request of a visitor to join the main meeting.
 *
 * @param {IPromotionRequest} request - The request from the visitor.
 * @param {PromotionTargetRole} role - The main-room role to grant.
 * @returns {Function}
 */
export function approveRequest(request, role = 'participant') {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'promotion-response',
            approved: true,
            role,
            id: request.from
        });
        dispatch(clearPromotionRequest(request));
    };
}
/**
 * Suspends or resumes chat for one visitor. Server-verified: Prosody checks the
 * sender is a moderator, blocks the visitor's messages on the main room and on
 * every visitor node, and broadcasts the confirmed state back to all moderators.
 *
 * @param {string} vid - The visitor's vid (vnode occupant resource, stamped on
 * their chat messages).
 * @param {boolean} on - True to suspend, false to resume.
 * @returns {Function}
 */
export function suspendVisitorChat(vid, on) {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'suspend-chat',
            vid,
            on
        });
    };
}
/**
 * Records the server-confirmed chat-suspension state of one visitor.
 *
 * @param {string} vid - The visitor's vid.
 * @param {boolean} on - Whether chat is suspended.
 * @returns {Object}
 */
export function setVisitorChatSuspended(vid, on) {
    return {
        type: SET_VISITOR_CHAT_SUSPENDED,
        vid,
        on
    };
}
/**
 * Stores the server-reported roster of the visitors currently on the room's
 * visitor nodes. Dispatched on the 'visitors-roster' snapshot the component
 * sends to a joining moderator.
 *
 * @param {IVisitor[]} visitors - The visitors roster.
 * @returns {Object}
 */
export function setVisitorsRoster(visitors) {
    return {
        type: SET_VISITORS_ROSTER,
        visitors
    };
}
/**
 * Adds (or replaces) one visitor in the roster.
 *
 * @param {IVisitor} visitor - The visitor that joined a visitor node.
 * @returns {Object}
 */
export function visitorJoined(visitor) {
    return {
        type: VISITOR_JOINED,
        visitor
    };
}
/**
 * Removes one visitor from the roster.
 *
 * @param {string} vid - The visitor's vid (vnode occupant resource).
 * @returns {Object}
 */
export function visitorLeft(vid) {
    return {
        type: VISITOR_LEFT,
        vid
    };
}
/**
 * Records the server-confirmed raise-hand (ask-to-join) block state of one
 * visitor.
 *
 * @param {string} vid - The visitor's vid.
 * @param {boolean} on - Whether raise hand is blocked.
 * @returns {Object}
 */
export function setVisitorHandBlocked(vid, on) {
    return {
        type: SET_VISITOR_HAND_BLOCKED,
        vid,
        on
    };
}
/**
 * Records whether a moderator blocked the local visitor's raise hand.
 * Server-confirmed only — the visitor hears it over the vnode's json channel.
 *
 * @param {boolean} on - Whether raise hand is blocked.
 * @returns {Object}
 */
export function setIAmHandBlocked(on) {
    return {
        type: SET_I_AM_HAND_BLOCKED,
        on
    };
}
/**
 * Records whether a moderator suspended the local visitor's chat.
 *
 * @param {boolean} on - Whether chat is suspended.
 * @returns {Object}
 */
export function setIAmChatSuspended(on) {
    return {
        type: SET_I_AM_CHAT_SUSPENDED,
        on
    };
}
/**
 * Records the room's confirmed raise-hand disable state for viewers as read
 * from room metadata {@code visitors.raiseHandDisabled}. Server truth only.
 *
 * @param {boolean} [value] - The confirmed value, or undefined when unset.
 * @returns {Object}
 */
export function setVisitorsRaiseHandDisabled(value) {
    return {
        type: SET_VISITORS_RAISE_HAND_DISABLED,
        value
    };
}
/**
 * Marks a moderator raise-hand block toggle request as in flight (or settles it).
 *
 * @param {boolean} pending - Whether a request is in flight.
 * @param {boolean} [requestedValue] - The value the pending request asked for.
 * @returns {Object}
 */
export function setRaiseHandDisabledTogglePending(pending, requestedValue) {
    return {
        type: SET_RAISE_HAND_DISABLED_TOGGLE_PENDING,
        pending,
        requestedValue
    };
}
/**
 * Stores the latest moderator raise-hand block toggle error.
 *
 * @param {string} [error] - The i18n key describing the error, or undefined to clear.
 * @returns {Object}
 */
export function setRaiseHandDisabledToggleError(error) {
    return {
        type: SET_RAISE_HAND_DISABLED_TOGGLE_ERROR,
        error
    };
}
/**
 * Moderator action that kicks one visitor out of the visitor node. Prosody
 * checks the sender is a moderator, removes the occupant on the vnode and the
 * resulting visitor-left update drops them from every moderator's roster.
 *
 * @param {string} vid - The visitor's vid.
 * @returns {Function}
 */
export function kickVisitor(vid) {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'kick-visitor',
            vid
        });
    };
}
/**
 * Blocks or unblocks raise hand (ask-to-join) for one visitor, identified by
 * vid. Server-verified: Prosody denies the visitor's promotion requests and
 * broadcasts the confirmed state back to all moderators.
 *
 * @param {string} vid - The visitor's vid.
 * @param {boolean} on - True to block, false to unblock.
 * @returns {Function}
 */
export function blockVisitorRaiseHand(vid, on) {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'block-raise-hand',
            vid,
            on
        });
    };
}
/**
 * Denies the request of a visitor to join the main meeting.
 *
 * @param {IPromotionRequest} request - The request from the visitor.
 * @returns {Function}
 */
export function denyRequest(request) {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        conference === null || conference === void 0 ? void 0 : conference.sendMessage({
            type: 'visitors',
            action: 'promotion-response',
            approved: false,
            id: request.from
        });
        dispatch(clearPromotionRequest(request));
    };
}
/**
 * Sends a demote request to a main participant to join the meeting as a visitor.
 *
 * @param {string} id - The ID for the participant.
 * @returns {Function}
 */
export function demoteRequest(id) {
    return (dispatch, getState) => {
        const conference = getCurrentConference(getState);
        const localParticipant = getLocalParticipant(getState());
        sendAnalytics(createRemoteVideoMenuButtonEvent('demote.button', { 'participant_id': id }));
        vlog('1. demoteRequest (moderator side)', {
            targetId: id,
            localId: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id,
            isSelf: id === (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id)
        });
        if (id === (localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id)) {
            vlog('1a. self-demote: disconnect -> setPreferVisitor -> connect');
            dispatch(setRoleTransition({ kind: 'to-viewer' }));
            dispatch(disconnectAny(true))
                .then(() => dispatch(setPreferVisitor(true)))
                .then(() => dispatch(reconnectAfterRoleChange()))
                .catch((e) => {
                verr('self-demote chain failed', e);
                dispatch(abortRoleTransition());
            });
        }
        else {
            vlog('1b. remote demote: sending "demote-request" message to target', { id,
                actor: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id });
            conference === null || conference === void 0 ? void 0 : conference.sendMessage({
                type: 'visitors',
                action: 'demote-request',
                id,
                actor: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id
            });
        }
    };
}
/**
 * Removes a promotion request from the state.
 *
 * @param {IPromotionRequest} request - The request.
 * @returns {{
 *     type: CLEAR_VISITOR_PROMOTION_REQUEST,
 *     request: IPromotionRequest
 * }}
 */
export function clearPromotionRequest(request) {
    return {
        type: CLEAR_VISITOR_PROMOTION_REQUEST,
        request
    };
}
/**
 * Visitor has sent us a promotion request.
 *
 * @param {IPromotionRequest} request - The request.
 * @returns {{
 *     type: VISITOR_PROMOTION_REQUEST,
 * }}
 */
export function promotionRequestReceived(request) {
    return {
        type: VISITOR_PROMOTION_REQUEST,
        request
    };
}
/**
 * Shows the loading screen for a move between the main meeting and viewer
 * mode, or clears it. The screen stays up across the disconnect, because
 * that disconnect is the move.
 *
 * @param {IRoleTransition} [transition] - The move to show, or undefined
 * to clear the screen.
 * @returns {{
 *     type: SET_ROLE_TRANSITION
 * }}
 */
export function setRoleTransition(transition) {
    return {
        type: SET_ROLE_TRANSITION,
        transition
    };
}
/**
 * Drops the loading screen after a move fails, and says so. A no-op when
 * nothing is in progress, so a failure that is reported twice (the chain
 * and CONFERENCE_FAILED) only tells the user once.
 *
 * @returns {Function}
 */
export function abortRoleTransition() {
    return (dispatch, getState) => {
        if (!getState()['features/visitors'].roleTransition) {
            return;
        }
        dispatch(setRoleTransition(undefined));
        dispatch(showNotification({
            titleKey: 'visitors.notification.title',
            descriptionKey: 'visitors.transition.failed'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    };
}
/**
 * Opens the connection the role move reconnects on. Web joins through the
 * legacy conference (it is what sends the conference request jicofo answers
 * with a redirect). Native has no APP, so it uses its own connection.
 *
 * @returns {Function}
 */
export function reconnectAfterRoleChange() {
    return (dispatch) => {
        if (navigator.product === 'ReactNative') {
            const { connect: connectNative } = require('../base/connection/actions.native');
            return dispatch(connectNative());
        }
        return dispatch(connect());
    };
}
/**
 * Sets Visitors mode on or off.
 *
 * @param {boolean} enabled - The new visitors mode state.
 * @returns {{
 *     type: I_AM_VISITOR_MODE,
 * }}
 */
export function setIAmVisitor(enabled) {
    return {
        type: I_AM_VISITOR_MODE,
        enabled
    };
}
/**
 * Sets visitor demote actor.
 *
 * @param {string|undefined} displayName - The display name of the participant.
 * @returns {{
 *     type: SET_VISITOR_DEMOTE_ACTOR,
 * }}
 */
export function setVisitorDemoteActor(displayName) {
    return {
        type: SET_VISITOR_DEMOTE_ACTOR,
        displayName
    };
}
/**
 * Visitors count has been updated.
 *
 * @param {boolean} value - The new value whether visitors are supported.
 * @returns {{
 *     type: SET_VISITORS_SUPPORTED,
 * }}
 */
export function setVisitorsSupported(value) {
    return {
        type: SET_VISITORS_SUPPORTED,
        value
    };
}
/**
 * Stores whether visitor mode is currently armed for the room (from backend metadata).
 *
 * @param {boolean} enabled - Whether visitor mode is armed for the room.
 * @param {string} [mode] - The active toggle mode.
 * @param {string} [updatedBy] - The moderator who last updated the mode.
 * @param {number} [updatedAt] - Timestamp of the last update.
 * @returns {{
 *     type: SET_VISITORS_ENABLED,
 * }}
 */
export function setVisitorsEnabled(enabled, mode, updatedBy, updatedAt) {
    return {
        type: SET_VISITORS_ENABLED,
        enabled,
        mode,
        updatedBy,
        updatedAt
    };
}
/**
 * Marks a moderator visitor-mode toggle request as pending or settled.
 *
 * @param {boolean} pending - Whether a toggle request is in flight.
 * @param {boolean} [requestedValue] - The requested visitor mode value.
 * @param {string} [requestedMode] - The requested toggle mode.
 * @returns {{
 *     type: SET_VISITOR_MODE_TOGGLE_PENDING,
 * }}
 */
export function setVisitorModeTogglePending(pending, requestedValue, requestedMode) {
    return {
        type: SET_VISITOR_MODE_TOGGLE_PENDING,
        pending,
        requestedValue,
        requestedMode
    };
}
/**
 * Stores the latest moderator visitor-mode toggle error.
 *
 * @param {string|undefined} error - The i18n key for the error to display, or undefined to clear it.
 * @returns {{
 *     type: SET_VISITOR_MODE_TOGGLE_ERROR,
 * }}
 */
export function setVisitorModeToggleError(error) {
    return {
        type: SET_VISITOR_MODE_TOGGLE_ERROR,
        error
    };
}
/**
 * Moderator action that turns visitor mode on or off for the room. The new state is persisted and
 * broadcast through room metadata; the local redux {@code enabled} flag is only updated once the
 * resulting {@code METADATA_UPDATED} event is received (handled in middleware), so we never decide
 * the room mode optimistically.
 *
 * @param {boolean} enabled - The desired (new) visitor mode state.
 * @param {string} [mode] - When enabling, whether to apply to 'new-guests-only' or 'move-current-guests'.
 * @param {boolean} [moveExistingGuests] - When enabling with 'move-current-guests', request migration of
 * existing non-moderator guests.
 * @returns {Function}
 */
export function toggleVisitorsMode(enabled, mode, moveExistingGuests = false) {
    return (dispatch, getState) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const state = getState();
        const conference = getCurrentConference(state);
        const metadataHandler = (_a = conference === null || conference === void 0 ? void 0 : conference.getMetadataHandler) === null || _a === void 0 ? void 0 : _a.call(conference);
        const localParticipant = getLocalParticipant(state);
        if (!conference || !metadataHandler || !((_b = metadataHandler.isSupported) === null || _b === void 0 ? void 0 : _b.call(metadataHandler))) {
            verr('toggleVisitorsMode: room metadata not available, cannot toggle visitor mode');
            dispatch(setVisitorModeToggleError('visitors.section.unavailable'));
            return;
        }
        // setMetadata replaces the whole 'visitors' object, so carry the
        // auto-promote setting through or toggling visitor mode would silently
        // reset it.
        const currentAutoPromote = (_e = (_d = (_c = metadataHandler.getMetadata) === null || _c === void 0 ? void 0 : _c.call(metadataHandler)) === null || _d === void 0 ? void 0 : _d[VISITORS_METADATA_KEY]) === null || _e === void 0 ? void 0 : _e.autoPromote;
        const currentRaiseHandDisabled = (_h = (_g = (_f = metadataHandler.getMetadata) === null || _f === void 0 ? void 0 : _f.call(metadataHandler)) === null || _g === void 0 ? void 0 : _g[VISITORS_METADATA_KEY]) === null || _h === void 0 ? void 0 : _h.raiseHandDisabled;
        const payload = {
            enabled,
            roleBased: true,
            mode: enabled ? mode : undefined,
            moveExistingGuests: enabled ? moveExistingGuests : false,
            autoPromote: typeof currentAutoPromote === 'boolean' ? currentAutoPromote : undefined,
            raiseHandDisabled: typeof currentRaiseHandDisabled === 'boolean'
                ? currentRaiseHandDisabled
                : undefined,
            updatedBy: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id,
            updatedAt: Date.now()
        };
        vlog('moderator toggling visitor mode', payload);
        dispatch(setVisitorModeTogglePending(true, enabled, mode));
        try {
            metadataHandler.setMetadata(VISITORS_METADATA_KEY, payload);
            // The pending flag is normally cleared when the resulting METADATA_UPDATED broadcast comes
            // back (see middleware). Guard against a missing broadcast so the switch never sticks.
            setTimeout(() => {
                if (getState()['features/visitors'].togglePending) {
                    verr('toggleVisitorsMode: no metadata broadcast received, clearing pending state');
                    dispatch(setVisitorModeToggleError('visitors.section.timeout'));
                    dispatch(setVisitorModeTogglePending(false));
                }
            }, 6000);
        }
        catch (e) {
            verr('toggleVisitorsMode: setMetadata failed', e);
            dispatch(setVisitorModeToggleError('visitors.section.error'));
            dispatch(setVisitorModeTogglePending(false));
        }
    };
}
/**
 * Records the room's confirmed auto-promote state as read from room metadata.
 * Server truth only — dispatched from the metadata broadcast, never optimistically.
 *
 * @param {boolean} [value] - The confirmed {@code visitors.autoPromote} value, or
 * undefined when the room has no explicit setting (the deployment default,
 * AUTO_ALLOW_VISITOR_PROMOTION, then applies server-side).
 * @returns {{
 *     type: SET_AUTO_PROMOTE_VISITORS,
 *     value: boolean | undefined
 * }}
 */
export function setAutoPromoteVisitors(value) {
    return {
        type: SET_AUTO_PROMOTE_VISITORS,
        value
    };
}
/**
 * Marks a moderator auto-promote toggle request as in flight (or settles it).
 *
 * @param {boolean} pending - Whether a request is in flight.
 * @param {boolean} [requestedValue] - The value the pending request asked for.
 * @returns {{
 *     type: SET_AUTO_PROMOTE_TOGGLE_PENDING,
 *     pending: boolean,
 *     requestedValue: boolean | undefined
 * }}
 */
export function setAutoPromoteTogglePending(pending, requestedValue) {
    return {
        type: SET_AUTO_PROMOTE_TOGGLE_PENDING,
        pending,
        requestedValue
    };
}
/**
 * Stores the latest moderator auto-promote toggle error.
 *
 * @param {string} [error] - The i18n key describing the error, or undefined to clear.
 * @returns {{
 *     type: SET_AUTO_PROMOTE_TOGGLE_ERROR,
 *     error: string | undefined
 * }}
 */
export function setAutoPromoteToggleError(error) {
    return {
        type: SET_AUTO_PROMOTE_TOGGLE_ERROR,
        error
    };
}
/**
 * Moderator action that turns automatic admission of visitor join requests on or
 * off for the room, persisted as room metadata {@code visitors.autoPromote}.
 * Prosody's visitors component reads it when a promotion request arrives
 * (OR-ed with the deployment-wide AUTO_ALLOW_VISITOR_PROMOTION default), so late
 * joiners and every moderator see the same server truth. The local flag is only
 * updated once the resulting METADATA_UPDATED broadcast comes back.
 *
 * @param {boolean} enabled - The desired auto-promote state.
 * @returns {Function}
 */
export function toggleAutoPromoteVisitors(enabled) {
    return (dispatch, getState) => {
        var _a, _b, _c, _d, _e;
        const state = getState();
        const conference = getCurrentConference(state);
        const metadataHandler = (_a = conference === null || conference === void 0 ? void 0 : conference.getMetadataHandler) === null || _a === void 0 ? void 0 : _a.call(conference);
        const localParticipant = getLocalParticipant(state);
        if (!conference || !metadataHandler || !((_b = metadataHandler.isSupported) === null || _b === void 0 ? void 0 : _b.call(metadataHandler))) {
            verr('toggleAutoPromoteVisitors: room metadata not available');
            dispatch(setAutoPromoteToggleError('visitors.section.unavailable'));
            return;
        }
        // setMetadata replaces the whole 'visitors' object, so preserve the
        // visitor-mode fields... with one exception: the one-shot migration
        // intent (mode 'move-current-guests' / moveExistingGuests) must be
        // neutralized, because mod_visitors_role_based re-applies any 'visitors'
        // write that carries toggle intent — replaying a stored migration would
        // demote every current main-room guest just because auto-admit was
        // flipped. Only rewrite those fields when they exist: adding them to a
        // room that never armed visitor mode would itself create toggle intent
        // (which prosody's normalize_toggle defaults to enabled=true).
        const current = (_e = (_d = (_c = metadataHandler.getMetadata) === null || _c === void 0 ? void 0 : _c.call(metadataHandler)) === null || _d === void 0 ? void 0 : _d[VISITORS_METADATA_KEY]) !== null && _e !== void 0 ? _e : {};
        const payload = Object.assign(Object.assign({}, current), { autoPromote: enabled, autoPromoteUpdatedBy: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id, autoPromoteUpdatedAt: Date.now() });
        if (payload.mode === 'move-current-guests') {
            payload.mode = 'new-guests-only';
        }
        if (payload.moveExistingGuests) {
            payload.moveExistingGuests = false;
        }
        vlog('moderator toggling visitor auto-promote', payload);
        dispatch(setAutoPromoteTogglePending(true, enabled));
        try {
            metadataHandler.setMetadata(VISITORS_METADATA_KEY, payload);
            // Cleared by the METADATA_UPDATED broadcast; guard against a missing
            // echo so the switch never sticks.
            setTimeout(() => {
                if (getState()['features/visitors'].autoPromotePending) {
                    verr('toggleAutoPromoteVisitors: no metadata broadcast received');
                    dispatch(setAutoPromoteToggleError('visitors.section.autoPromoteTimeout'));
                    dispatch(setAutoPromoteTogglePending(false));
                }
            }, 6000);
        }
        catch (e) {
            verr('toggleAutoPromoteVisitors: setMetadata failed', e);
            dispatch(setAutoPromoteToggleError('visitors.section.autoPromoteError'));
            dispatch(setAutoPromoteTogglePending(false));
        }
    };
}
/**
 * Moderator action that blocks or unblocks raise hand for every viewer in the
 * room, persisted as room metadata {@code visitors.raiseHandDisabled}.
 * Prosody's visitors component reads it when a promotion request arrives and
 * denies the raise on the spot. The local flag is only updated once the
 * resulting METADATA_UPDATED broadcast comes back.
 *
 * @param {boolean} enabled - The desired room-wide raise-hand block state.
 * @returns {Function}
 */
export function toggleVisitorsRaiseHandDisabled(enabled) {
    return (dispatch, getState) => {
        var _a, _b, _c, _d, _e;
        const state = getState();
        const conference = getCurrentConference(state);
        const metadataHandler = (_a = conference === null || conference === void 0 ? void 0 : conference.getMetadataHandler) === null || _a === void 0 ? void 0 : _a.call(conference);
        const localParticipant = getLocalParticipant(state);
        if (!conference || !metadataHandler || !((_b = metadataHandler.isSupported) === null || _b === void 0 ? void 0 : _b.call(metadataHandler))) {
            verr('toggleVisitorsRaiseHandDisabled: room metadata not available');
            dispatch(setRaiseHandDisabledToggleError('visitors.section.unavailable'));
            return;
        }
        // setMetadata replaces the whole 'visitors' object: carry every known
        // key through, and neutralize the one-shot migration intent so this
        // write cannot re-trigger a visitor-mode toggle (same rule as the
        // autoPromote writer follows).
        const current = (_e = (_d = (_c = metadataHandler.getMetadata) === null || _c === void 0 ? void 0 : _c.call(metadataHandler)) === null || _d === void 0 ? void 0 : _d[VISITORS_METADATA_KEY]) !== null && _e !== void 0 ? _e : {};
        const payload = Object.assign(Object.assign({}, current), { raiseHandDisabled: enabled, updatedBy: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.id, updatedAt: Date.now() });
        if (payload.mode === 'move-current-guests') {
            payload.mode = 'new-guests-only';
        }
        if (payload.moveExistingGuests) {
            payload.moveExistingGuests = false;
        }
        vlog('moderator toggling raise hand for viewers', payload);
        dispatch(setRaiseHandDisabledTogglePending(true, enabled));
        try {
            metadataHandler.setMetadata(VISITORS_METADATA_KEY, payload);
            // Cleared by the METADATA_UPDATED broadcast; guard against a missing
            // echo so the switch never sticks.
            setTimeout(() => {
                if (getState()['features/visitors'].raiseHandDisabledPending) {
                    verr('toggleVisitorsRaiseHandDisabled: no metadata broadcast received');
                    dispatch(setRaiseHandDisabledToggleError('visitors.section.raiseHandDisabledTimeout'));
                    dispatch(setRaiseHandDisabledTogglePending(false));
                }
            }, 6000);
        }
        catch (e) {
            verr('toggleVisitorsRaiseHandDisabled: setMetadata failed', e);
            dispatch(setRaiseHandDisabledToggleError('visitors.section.raiseHandDisabledError'));
            dispatch(setRaiseHandDisabledTogglePending(false));
        }
    };
}
/**
 * Visitors count has been updated.
 *
 * @param {number} count - The new visitors count.
 * @returns {{
 *     type: UPDATE_VISITORS_COUNT,
 * }}
 */
export function updateVisitorsCount(count) {
    return {
        type: UPDATE_VISITORS_COUNT,
        count
    };
}
/**
 * Arms or disarms visitor mode for a room from the pre-join screen, before
 * anybody is in it.
 *
 * The in-meeting toggle ({@link toggleVisitorsMode}) writes room metadata, which
 * needs a joined conference to write through. This one rides the pre-join XMPP
 * session instead: Prosody verifies the moderator claim on the JWT that
 * authenticated it and remembers the decision until the room exists.
 *
 * Why it matters: jicofo decides host-vs-visitor when it answers a client's
 * conference request. An audience that opens the link before the host therefore
 * gets routed by whatever the room said at that moment, so a meeting that is
 * meant to run in visitor mode has to say so before the host arrives, not after.
 *
 * @param {string} roomName - Backend-safe room name.
 * @param {boolean} enabled - The desired (new) state.
 * @returns {Function}
 */
export function setPrejoinVisitorsMode(roomName, enabled) {
    return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
        if (!roomName) {
            return;
        }
        const previous = getState()['features/visitors'].prejoinEnabled;
        dispatch({
            type: SET_PREJOIN_VISITORS_MODE,
            enabled,
            pending: true
        });
        const accepted = yield dispatch(configureConference(roomName, {
            visitors: String(enabled)
        }));
        if (!accepted) {
            verr('setPrejoinVisitorsMode: server refused the change');
        }
        // Server truth only: a refusal puts the switch back where it was rather
        // than leaving the moderator believing a meeting is armed when it is not.
        dispatch({
            type: SET_PREJOIN_VISITORS_MODE,
            enabled: accepted ? enabled : previous,
            pending: false
        });
    });
}
